/**
 * Robust JSON extraction and repair for LLM output.
 *
 * LLMs frequently produce "almost-valid" JSON with defects:
 * - Markdown code fences wrapping the JSON
 * - Single-quoted strings instead of double-quoted
 * - Trailing commas before closing brackets
 * - Literal newlines inside string values
 * - Preamble text before the actual JSON
 *
 * This module handles all of these cases.
 */

/** Strip markdown fences and find the JSON structure in LLM output. */
function extractJsonString(raw: string): string {
  // Strip markdown code fences
  let stripped = raw
    .replace(/```(?:json)?\s*/gi, "")
    .replace(/```\s*/g, "")
    .trim();
  // Find outermost JSON array or object
  const arrMatch = stripped.match(/\[[\s\S]*\]/);
  if (arrMatch) return arrMatch[0];
  const objMatch = stripped.match(/\{[\s\S]*\}/);
  if (objMatch) return objMatch[0];
  return stripped;
}

/** Fix trailing commas: [1, 2, 3,] → [1, 2, 3] */
function fixTrailingCommas(json: string): string {
  return json.replace(/,\s*([}\]])/g, "$1");
}

/**
 * Detect if the JSON uses single-quoted string delimiters.
 * Checks for patterns like {'key': or ['value' that indicate
 * single-quote-delimited JSON (as opposed to prose containing apostrophes).
 */
function usesSingleQuoteDelimiters(json: string): boolean {
  // Look for single-quoted keys: 'key':
  return /[{[,]\s*'[^']*'\s*:/.test(json);
}

/**
 * Convert single-quoted JSON to double-quoted.
 * Only called when we detect the JSON actually uses single-quote delimiters.
 * Replaces all single quotes with double quotes, then escapes any double
 * quotes that were inside the original single-quoted values.
 */
function fixSingleQuotes(json: string): string {
  return json
    .replace(/([{[,])\s*'/g, '$1"') // opening quote after { [ ,
    .replace(/'\s*:/g, '":') // closing quote before :
    .replace(/:\s*'/g, ':"') // opening quote after :
    .replace(/'\s*([,}\]])/g, '"$1'); // closing quote before , } ]
}

/**
 * Salvage complete objects from a truncated JSON array.
 * If the LLM ran out of tokens mid-array, find all complete objects
 * and close the array. Returns null if no complete objects found.
 */
function salvageTruncatedArray(json: string): string | null {
  // eslint-disable-next-line no-control-regex
  const flat = json.replace(/\r?\n/g, " ").replace(/[\x00-\x1f]/g, "");
  if (!flat.trimStart().startsWith("[")) return null;
  // Find all complete JSON objects using brace matching
  const objects: string[] = [];
  let depth = 0;
  let start = -1;
  for (let i = 0; i < flat.length; i++) {
    const ch = flat[i];
    if (ch === '"') {
      // Skip string contents
      i++;
      while (i < flat.length && flat[i] !== '"') {
        if (flat[i] === "\\") i++; // skip escaped char
        i++;
      }
    } else if (ch === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0 && start >= 0) {
        objects.push(flat.substring(start, i + 1));
        start = -1;
      }
    }
  }
  if (objects.length === 0) return null;
  return `[${objects.join(",")}]`;
}

/** Collapse literal newlines to spaces and strip control characters. */
function fixNewlines(json: string): string {
  // eslint-disable-next-line no-control-regex
  return json.replace(/\r?\n/g, " ").replace(/[\x00-\x1f]/g, "");
}

/**
 * Extract and parse JSON from LLM output, with progressive repair.
 * Tries direct parse first, then increasingly aggressive repairs.
 * Throws if nothing works.
 */
export function parseLlmJson<T = unknown>(raw: string): T {
  const json = extractJsonString(raw);

  // 1. Try direct parse (fast path for well-formed output)
  try {
    return JSON.parse(json) as T;
  } catch {
    // continue to repairs
  }

  // 2. Fix trailing commas only
  const noTrailing = fixTrailingCommas(json);
  try {
    return JSON.parse(noTrailing) as T;
  } catch {
    // continue
  }

  // 3. Collapse newlines (common: LLM puts literal newlines in string values)
  const noNewlines = fixNewlines(noTrailing);
  try {
    return JSON.parse(noNewlines) as T;
  } catch {
    // continue
  }

  // 4. Fix single-quoted strings — ONLY if we detect single-quote delimiters.
  // This avoids destroying prose that contains apostrophes/dialogue quotes.
  if (usesSingleQuoteDelimiters(noNewlines)) {
    const fixed = fixSingleQuotes(noNewlines);
    try {
      return JSON.parse(fixed) as T;
    } catch {
      // continue
    }
  }

  // 5. Truncation recovery: if the JSON was cut short by maxTokens,
  // try to salvage complete objects from a truncated array.
  const truncated = salvageTruncatedArray(noNewlines);
  if (truncated) {
    try {
      return JSON.parse(truncated) as T;
    } catch {
      // continue
    }
  }

  // 6. Last resort: try collapsing newlines on the original (before trailing comma fix)
  const lastResort = fixNewlines(json);
  try {
    return JSON.parse(lastResort) as T;
  } catch (err) {
    throw new SyntaxError(
      `Could not parse LLM JSON after repairs: ${err instanceof Error ? err.message : String(err)}\nInput: ${json.substring(0, 200)}`,
    );
  }
}
